import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, Send, Clock as ClockIcon } from 'lucide-react';
import { enqueueOperation } from '../db/offlineStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQUEST_KINDS = [
  { value: 'detention',            label: 'Detention pay',       fields: ['hours'] },
  { value: 'tonu',                 label: 'TONU (Truck Order Not Used)', fields: ['amount_usd'] },
  { value: 'layover',              label: 'Layover',             fields: ['hours'] },
  { value: 'breakdown',            label: 'Breakdown / Roadside', fields: [] },
  { value: 'lumper_reimbursement', label: 'Lumper reimbursement', fields: ['amount_usd'] },
  { value: 'toll_reimbursement',   label: 'Toll reimbursement',  fields: ['amount_usd'] },
  { value: 'fuel_advance',         label: 'Fuel advance',        fields: ['amount_usd'] },
  { value: 'cash_advance',         label: 'Cash advance',        fields: ['amount_usd'] },
  { value: 'appointment_change',   label: 'Appointment change',  fields: [] },
  { value: 'note',                 label: 'General note',        fields: [] },
  { value: 'other',                label: 'Other',               fields: [] },
];

const _tryCaptureGps = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  const timeout = setTimeout(() => resolve(null), 4000);
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      clearTimeout(timeout);
      resolve({
        gps_lat: pos.coords.latitude,
        gps_lng: pos.coords.longitude,
        gps_accuracy_m: pos.coords.accuracy,
        captured_at: new Date().toISOString(),
      });
    },
    () => { clearTimeout(timeout); resolve(null); },
    { enableHighAccuracy: false, maximumAge: 60_000, timeout: 3500 },
  );
});

const GPS_ATTACH_KINDS = new Set(['detention', 'breakdown', 'layover', 'tonu', 'appointment_change']);

/**
 * Driver-facing modal for filing operational requests / notes.
 * Works fully offline: if navigator.onLine is false, the request is
 * enqueued to IndexedDB with a UUID and drained on reconnect. Same
 * `operation_id` in-flight guarantees idempotent server-side replay.
 */
const DriverRequestModal = ({ open, loadId, onClose, onSubmitted }) => {
  const [kind, setKind] = useState('detention');
  const [body, setBody] = useState('');
  const [amount, setAmount] = useState('');
  const [hours, setHours] = useState('');
  const [saving, setSaving] = useState(false);

  const kindMeta = REQUEST_KINDS.find((k) => k.value === kind) || REQUEST_KINDS[0];
  const showAmount = kindMeta.fields.includes('amount_usd');
  const showHours = kindMeta.fields.includes('hours');

  if (!open) return null;

  const reset = () => {
    setBody(''); setAmount(''); setHours(''); setKind('detention');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() && kind === 'note') {
      toast.error('Notes need at least a short message.');
      return;
    }
    setSaving(true);
    const opId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `op-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    // Attach a forensic GPS fingerprint for time-sensitive requests so
    // dispatch can prove exactly WHEN and WHERE the clock started for
    // detention/breakdown/TONU billing. Non-blocking — if the user
    // declines the permission prompt or geolocation isn't available we
    // just skip it.
    let gps = null;
    if (GPS_ATTACH_KINDS.has(kind)) {
      gps = await _tryCaptureGps();
    }
    const payload = {
      kind,
      body: body.trim(),
      load_id: loadId || null,
      amount_usd: showAmount && amount ? parseFloat(amount) : null,
      hours: showHours && hours ? parseFloat(hours) : null,
      ...(gps || {}),
    };

    // Offline path: queue only, close modal, banner will drain on reconnect.
    if (!navigator.onLine) {
      try {
        await enqueueOperation({ opId, kind: 'driver_request', loadId: loadId || null, payload });
        toast.success('Saved offline — dispatch will get this the moment you\u2019re back online.');
        reset();
        onSubmitted?.();
        onClose();
      } catch {
        toast.error('Could not save offline — please try again when back online.');
      } finally {
        setSaving(false);
      }
      return;
    }

    try {
      await axios.post(
        `${API}/driver-requests`,
        { ...payload, operation_id: opId },
        { withCredentials: true }
      );
      toast.success('Request sent to dispatch.');
      reset();
      onSubmitted?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="driver-request-modal">
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-xl shadow-xl">
        <header className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            New request
          </h2>
          <button
            data-testid="driver-request-close"
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </header>

        <form onSubmit={submit} className="px-5 py-5 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Type</label>
            <select
              data-testid="driver-request-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
            >
              {REQUEST_KINDS.map((k) => (
                <option key={k.value} value={k.value}>{k.label}</option>
              ))}
            </select>
          </div>

          {(showAmount || showHours) && (
            <div className="grid grid-cols-2 gap-3">
              {showAmount && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Amount ($)</label>
                  <input
                    data-testid="driver-request-amount"
                    type="number" step="0.01" min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                  />
                </div>
              )}
              {showHours && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Hours</label>
                  <input
                    data-testid="driver-request-hours"
                    type="number" step="0.25" min="0"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                  />
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Details</label>
            <textarea
              data-testid="driver-request-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              placeholder="Add context — dispatch will see this immediately."
              maxLength={2000}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
            />
          </div>

          {!navigator.onLine && (
            <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-amber-900 text-xs">
              <ClockIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>You&rsquo;re offline. This request will be saved on your device and sent to dispatch as soon as you reconnect.</span>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="driver-request-submit"
              disabled={saving}
              className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#00247D] text-white text-sm font-semibold rounded-md px-4 py-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {navigator.onLine ? 'Send' : 'Save offline'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DriverRequestModal;
